import { createSlice } from "@reduxjs/toolkit";
import { fetchCampaignBarsStats, fetchDonationStats, fetchDonorsStats, fetchShipmentStats, fetchDonorsInfo, fetchLeaderBoardInfo, fetchReferralLeaderboard, fetchFilteredDashboardStats, fetchDashboardCampaigns } from "../../services/DashboardService";
import { CampaignDataType, DashboardCampaignOption, DonationStats, DonorsInfo, DonorsStats, FilteredDashboardStats, LeaderInfo, ReferralPerformance, shipmentStats } from "../../types/dashboard.types";

interface DashboardState {
    donationStats: DonationStats;
    shipmentStats: shipmentStats;
    error: string | undefined;
    donorsStats: DonorsStats;
    loading: boolean;
    campaignBars: CampaignDataType[];
    donorsInfo: DonorsInfo;
    leadersInfo: LeaderInfo[];
    referralLeaderboard: ReferralPerformance[];
    referralLeaderboardLoading: boolean;
    filteredStats: FilteredDashboardStats;
    filteredStatsLoading: boolean;
    campaignsList: DashboardCampaignOption[];
    campaignsListLoading: boolean;
}

const initialFilteredStats: FilteredDashboardStats = {
    totalBars: 0,
    totalDonationAmount: 0,
    unsyncedTap2FeedDonationAmount: 0,
    tap2FeedBalanceAmount: 0,
    trackYourImpactDonationAmount: 0,
    syncedTap2FeedDonationAmount: 0,
    totalRegistrations: 0,
    inviteFOODIEClicks: 0,
    sharedClicks: 0,
    ordersWaitingFullfillment: 0,
    fulfilledWaitingAssignment: 0,
    campaignBars: [],
    leaderboard: [],
};

const initialState: DashboardState = {
    donationStats: {
        campaignStats: [{
            totalAmount: 0,
        }],
    },
    shipmentStats: {
        ordersWaitingFullfillment: 0,
        fulfilledWaitingAssignment: 0,
    },
    error: undefined,
    loading: true,
    donorsStats: {
        appSignups: [],
        inviteFOODIEClicks: 0,
        sharedClicks: 0
    },
    campaignBars: [],
    donorsInfo: {
        countries: []
    },
    leadersInfo: [],
    referralLeaderboard: [],
    referralLeaderboardLoading: false,
    filteredStats: initialFilteredStats,
    filteredStatsLoading: false,
    campaignsList: [],
    campaignsListLoading: false,
};

const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDonationStats.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.donationStats = payload?.data;
                state.error = undefined;
            })
            .addCase(fetchDonationStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.donationStats = {
                    campaignStats: [{
                        totalAmount: 0,
                    }],
                }
            }).addCase(fetchShipmentStats.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.shipmentStats = payload?.data;
                state.error = undefined;
            })
            .addCase(fetchShipmentStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.shipmentStats = {
                    ordersWaitingFullfillment: 0,
                    fulfilledWaitingAssignment: 0
                }
            })
            .addCase(fetchDonorsStats.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.donorsStats = payload?.data;
                state.error = undefined;
            })
            .addCase(fetchDonorsStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.donorsStats = {
                    appSignups: [],
                    inviteFOODIEClicks: 0,
                    sharedClicks: 0
                }
            })
            .addCase(fetchCampaignBarsStats.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.campaignBars = payload?.data?.campaignStats;
                state.error = undefined;
            })
            .addCase(fetchCampaignBarsStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.campaignBars = []
            })
            .addCase(fetchDonorsInfo.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.donorsInfo = payload?.data;
                state.error = undefined;
            })
            .addCase(fetchDonorsInfo.rejected, (state, { payload }) => {
                state.loading = false;
                state.donorsInfo.countries = [];
                state.error = undefined;
            })
            .addCase(fetchLeaderBoardInfo.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.leadersInfo = payload?.data;
                state.error = undefined;
            }).addCase(fetchLeaderBoardInfo.rejected, (state, { payload }) => {
                state.loading = false;
                state.leadersInfo = [];
                state.error = undefined;
            })
            .addCase(fetchReferralLeaderboard.pending, (state) => {
                state.referralLeaderboardLoading = true;
            })
            .addCase(fetchReferralLeaderboard.fulfilled, (state, { payload }) => {
                state.referralLeaderboardLoading = false;
                state.referralLeaderboard = payload?.data || [];
                state.error = undefined;
            })
            .addCase(fetchReferralLeaderboard.rejected, (state, action) => {
                state.referralLeaderboardLoading = false;
                state.referralLeaderboard = [];
                state.error = action.error.message;
            })
            .addCase(fetchFilteredDashboardStats.pending, (state) => {
                state.filteredStatsLoading = true;
            })
            .addCase(fetchFilteredDashboardStats.fulfilled, (state, { payload }) => {
                state.filteredStatsLoading = false;
                state.filteredStats = payload?.data ?? initialFilteredStats;
                state.error = undefined;
            })
            .addCase(fetchFilteredDashboardStats.rejected, (state, action) => {
                state.filteredStatsLoading = false;
                state.filteredStats = initialFilteredStats;
                state.error = action.error.message;
            })
            .addCase(fetchDashboardCampaigns.pending, (state) => {
                state.campaignsListLoading = true;
            })
            .addCase(fetchDashboardCampaigns.fulfilled, (state, { payload }) => {
                state.campaignsListLoading = false;
                state.campaignsList = Array.isArray(payload?.data) ? payload.data : [];
                state.error = undefined;
            })
            .addCase(fetchDashboardCampaigns.rejected, (state, action) => {
                state.campaignsListLoading = false;
                state.campaignsList = [];
                state.error = action.error.message;
            })
    },
});

export default dashboardSlice.reducer;
